import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../../core/api/interceptors/axiosInterceptor';

/**
 * Variant Attribute Redux Slice
 * Manages global variant attributes (Color, Size, Storage, etc.)
 * Token-optimized, scalable, and reusable
 */

// Helper function to normalize API response (PascalCase to camelCase)
const normalizeAttribute = (attr) => {
  if (!attr) return null;
  
  // Handle values - could be array or JSON string
  let values = attr.Values || attr.values || [];
  if (typeof values === 'string') {
    try {
      values = JSON.parse(values);
    } catch (e) {
      // If not valid JSON, split by comma
      values = values.split(',').map(v => v.trim()).filter(Boolean);
    }
  }
  
  // If values is an array of objects with Value property, extract just the strings
  if (Array.isArray(values) && values.length > 0 && typeof values[0] === 'object' && values[0] !== null) {
    values = values.map(v => v.Value || v.value || v);
  }
  
  const normalized = {
    id: attr.Id || attr.id,
    name: attr.AttributeName || attr.name,
    values: Array.isArray(values) ? values : []
  };
  
  return normalized;
};

const normalizeAttributes = (attributes) => {
  if (!Array.isArray(attributes)) return [];
  return attributes.map(normalizeAttribute);
};

// Async thunks for API operations
export const fetchVariantAttributes = createAsyncThunk(
  'variantAttributes/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('VariantAttribute');
      const data = response.data?.payload || response.data || [];
      return normalizeAttributes(data);
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error
        || (Array.isArray(error.response?.data) ? error.response.data.join(', ') : null)
        || error.message 
        || 'Failed to fetch variant attributes';
      return rejectWithValue(errorMessage);
    }
  }
);

export const createVariantAttribute = createAsyncThunk(
  'variantAttributes/create',
  async (attributeData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('VariantAttribute/Create-Attribute-with-values', attributeData);
      const data = response.data?.payload || response.data;
      return normalizeAttribute(data);
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error
        || (Array.isArray(error.response?.data) ? error.response.data.join(', ') : null)
        || error.message 
        || 'Failed to create variant attribute';
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateVariantAttribute = createAsyncThunk(
  'variantAttributes/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(`VariantAttribute/${id}`, data);
      const responseData = response.data?.payload || response.data;
      return normalizeAttribute(responseData);
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error
        || (Array.isArray(error.response?.data) ? error.response.data.join(', ') : null)
        || error.message 
        || 'Failed to update variant attribute';
      return rejectWithValue(errorMessage);
    }
  }
);

export const deleteVariantAttribute = createAsyncThunk(
  'variantAttributes/delete',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`VariantAttribute/${id}`);
      return id;
    } catch (error) {
      const errorMessage = error.response?.data?.message 
        || error.response?.data?.error
        || (Array.isArray(error.response?.data) ? error.response.data.join(', ') : null)
        || error.message 
        || 'Failed to delete variant attribute';
      return rejectWithValue(errorMessage);
    }
  }
);

const variantAttributeSlice = createSlice({
  name: 'variantAttributes',
  initialState: {
    attributes: [],
    loading: false,
    error: null,
    lastFetched: null,
  },
  reducers: {
    clearAttributeError: (state) => {
      state.error = null;
    },
    // Local state management for offline-first approach
    addAttributeLocally: (state, action) => {
      state.attributes.push({
        ...action.payload,
        id: action.payload.id || Date.now(),
        isLocal: true,
      });
    },
    updateAttributeLocally: (state, action) => {
      const index = state.attributes.findIndex(attr => attr.id === action.payload.id);
      if (index !== -1) {
        state.attributes[index] = { ...state.attributes[index], ...action.payload };
      }
    },
    deleteAttributeLocally: (state, action) => {
      state.attributes = state.attributes.filter(attr => attr.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all attributes
      .addCase(fetchVariantAttributes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVariantAttributes.fulfilled, (state, action) => {
        state.loading = false;
        state.attributes = action.payload;
        state.lastFetched = Date.now();
      })
      .addCase(fetchVariantAttributes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create attribute
      .addCase(createVariantAttribute.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createVariantAttribute.fulfilled, (state, action) => {
        state.loading = false;
        state.attributes.push(action.payload);
      })
      .addCase(createVariantAttribute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update attribute
      .addCase(updateVariantAttribute.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateVariantAttribute.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.attributes.findIndex(attr => attr.id === action.payload.id);
        if (index !== -1) {
          state.attributes[index] = action.payload;
        }
      })
      .addCase(updateVariantAttribute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete attribute
      .addCase(deleteVariantAttribute.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteVariantAttribute.fulfilled, (state, action) => {
        state.loading = false;
        state.attributes = state.attributes.filter(attr => attr.id !== action.payload);
      })
      .addCase(deleteVariantAttribute.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearAttributeError,
  addAttributeLocally,
  updateAttributeLocally,
  deleteAttributeLocally,
} = variantAttributeSlice.actions;

export default variantAttributeSlice.reducer;
